import * as React from "react";
import { CollectionNewSearchProps } from "./CollectionNewSearchProps.csharp";
import { CollectionNewSearch } from "./CollectionSearch/CollectionNewSearch";
import { CollectionList } from "./CollectionList/CollectionList";
import { ICollectionListItem } from "./CollectionListItem/CollectionListItem";
import { MotifRelationship } from "./MotifRelationship";
import { Loader } from "../Ecommerce/Shared/Loader";

export interface SearchParams {
    sortingOption: string;
    showMainCollection: boolean;
    showDisplayed: boolean;
    showAll: boolean;
    classification: string;
    years: string;
    themes: string[];
    motifs: string[];
    exhibitions: string[];
}

export const CollectionNewSearchPage = (props: CollectionNewSearchProps) => {
    const [mounted, setMounted] = React.useState(false);

    const [url, setUrl] = React.useState<string>("");
    const [query, setQuery] = React.useState<string>("");
    const [results, setResults] = React.useState<ICollectionListItem[]>([]);
    const [page, setPage] = React.useState<number>(1);
    const [hasMoreResults, setHasMoreResults] = React.useState<boolean>(true);
    const [totalCount, setTotalCount] = React.useState<number>(0);
    const [totalCountIncludingHidden, setTotalCountIncludingHidden] = React.useState<number | null>(null);
    const [resultIsLoading, setResultIsLoading] = React.useState<boolean>(false);

    const [filterAllMotifs, setFilterAllMotifs] = React.useState<MotifRelationship | null>(null);
    const [filterAllThemes, setFilterAllThemes] = React.useState<MotifRelationship | null>(null);
    const [filterAllExhibitions, setFilterAllExhibitions] = React.useState<MotifRelationship | null>(null);
    const [motifFacetCounts, setMotifFacetCounts] = React.useState<{ [key: string]: number }>({});
    const [themeFacetCounts, setThemeFacetCounts] = React.useState<{ [key: string]: number }>({});
    const [exhibitionFacetCounts, setExhibitionFacetCounts] = React.useState<{ [key: string]: number }>({});

    const [searchParams, setSearchParams] = React.useState<SearchParams>({
        sortingOption: "default",
        showMainCollection: false,
        showDisplayed: false,
        showAll: true,
        classification: "",
        years: "",
        themes: [],
        motifs: [],
        exhibitions: [],
    });

    const [minYear, setMinYear] = React.useState<number>(1863);
    const [maxYear, setMaxYear] = React.useState<number>(1944);
    const [activeFilters, setActiveFilters] = React.useState<number>(0);
    const [filterIsOpen, setFilterIsOpen] = React.useState<boolean>(false);

    const [shouldScroll, setShouldScroll] = React.useState<boolean>(false);

    const resultsRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const filtersCount = [
            searchParams.classification !== "",
            minYear !== 1863 || maxYear !== 1944,
            searchParams.themes.length > 0,
            searchParams.motifs.length > 0,
            searchParams.exhibitions.length > 0,
        ].filter(Boolean).length;

        setActiveFilters(filtersCount);
    }, [searchParams, minYear, maxYear]);

    React.useEffect(() => {
        const baseUrl = window.location.href.split("?")[0];
        setUrl(baseUrl);

        fetchFilterData(baseUrl);

        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get("query") || "";
        const pageParam = parseInt(urlParams.get("page") || "1", 10);

        setQuery(queryParam);

        const yearsParam = urlParams.get("years");
        let initialMinYear = 1863;
        let initialMaxYear = 1944;
        if (yearsParam) {
            const yearsArray = yearsParam.split("|");
            if (yearsArray.length === 2) {
                initialMinYear = parseInt(yearsArray[0], 10) || 1863;
                initialMaxYear = parseInt(yearsArray[1], 10) || 1944;
            }
        }
        setMinYear(initialMinYear);
        setMaxYear(initialMaxYear);

        const themesParam = urlParams.getAll("theme");
        const motifsParam = urlParams.getAll("motif");
        const exhibitionsParam = urlParams.getAll("exhibition");
        const initialSearchParams = {
            sortingOption: urlParams.get("sortOrder") || "default",
            showMainCollection: urlParams.get("munchSamlingen") === "true",
            showDisplayed: urlParams.get("showDisplayed") === "true",
            showAll: urlParams.has("showAll") ? urlParams.get("showAll") === "true" : true,
            classification: urlParams.get("classification") || "",
            years: yearsParam || `${initialMinYear}|${initialMaxYear}`,
            themes: themesParam || [],
            motifs: motifsParam || [],
            exhibitions: exhibitionsParam || [],
        };
        setSearchParams(initialSearchParams);
        setPage(pageParam);

        const fetchAllPages = async () => {
            for (let p = 1; p <= pageParam; p++) {
                await fetchSearchResults(queryParam, p, initialSearchParams, p === 1, baseUrl);
            }
        };
        fetchAllPages().then(() => {
            setMounted(true);
        });
    }, []);

    React.useEffect(() => {
        if (mounted && shouldScroll && resultsRef.current && results.length > 0 && page === 1) {
            resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            setShouldScroll(false);
        }
    }, [results, page, mounted, shouldScroll]);

    const handleSearchChange = (newQuery?: string, newSearchParams?: Partial<SearchParams>) => {
        const finalQuery = newQuery !== undefined ? newQuery : query;
        const finalParams = { ...searchParams, ...newSearchParams };

        setShouldScroll(true);
        setPage(1);
        setResults([]);

        setQuery(finalQuery);
        setSearchParams(finalParams);

        const updatedSearchParams = {
            ...finalParams,
            years: `${minYear}|${maxYear}`,
        };
        updateURL(finalQuery, 1, updatedSearchParams);

        fetchSearchResults(finalQuery, 1, updatedSearchParams, true, url);
        fetchFilterData(url, finalQuery, updatedSearchParams);
    };

    const setSearchParamsWrapper: React.Dispatch<React.SetStateAction<SearchParams>> = (value) => {
        if (typeof value === "function") {
            const newParams = value(searchParams);
            handleSearchChange(query, newParams);
        } else {
            handleSearchChange(query, value);
        }
    };

    const handleShowMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        updateURL(query, nextPage, searchParams);
        fetchSearchResults(query, nextPage, searchParams, false, url);
    };

    const fetchSearchResults = async (
        searchQuery: string,
        pageNumber: number,
        searchOptions: SearchParams,
        replaceResults: boolean = false,
        _url = url
    ) => {
        setResultIsLoading(true);

        const queryString = buildQueryString(searchQuery, pageNumber, searchOptions);
        const apiUrl = `${_url}/Search${queryString}`;

        try {
            const response = await fetch(apiUrl, { credentials: "same-origin" });
            const data = await response.json();

            setResultIsLoading(false);
            setHasMoreResults(data.results.hasNext);
            setTotalCount(data.results.totalCount);
            setTotalCountIncludingHidden(data.totalCountIncludingHidden);

            const rawResults = data.results.items;
            setResults((prev) => (replaceResults ? rawResults : [...prev, ...rawResults]));
        } catch (error) {
            console.error("Error fetching search results:", error);
            setResultIsLoading(false);
        }
    };

    const formatCount = (value: number | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }

        return value.toLocaleString("en-US").replace(/,/g, " ");
    };

    const fetchFilterData = (baseUrl: string, searchQuery: string = "", searchOptions: SearchParams = searchParams) => {
        const urlParams = buildURLParams(searchQuery, 1, { ...searchOptions, themes: [], motifs: [], exhibitions: [] });
        const apiUrl = `${baseUrl}/GetFilterData?${urlParams.toString()}`;

        fetch(apiUrl, { credentials: "same-origin" })
            .then((response) => response.json())
            .then((response) => {
                setFilterAllMotifs(response.motifFilters[0] || null);
                setFilterAllThemes(response.themeFilters[0] || null);

                const motifFacet = response.facets.find((f: any) => f.name === "Motif");
                if (motifFacet) {
                    const counts: { [key: string]: number } = {};
                    motifFacet.availableFacets.forEach((item: any) => {
                        counts[item.key] = item.count;
                    });
                    setMotifFacetCounts(counts);
                }

                const themeFacet = response.facets.find((f: any) => f.name === "Theme");
                if (themeFacet) {
                    const counts: { [key: string]: number } = {};
                    themeFacet.availableFacets.forEach((item: any) => {
                        counts[item.key] = item.count;
                    });
                    setThemeFacetCounts(counts);
                }

                const exhibitionFacet = response.facets.find((f: any) => f.name === "Exhibitions");
                if (exhibitionFacet) {
                    const counts: { [key: string]: number } = {};
                    const children: MotifRelationship[] = exhibitionFacet.availableFacets.map((item: any) => {
                        counts[item.key] = item.count;
                        return { parentName: item.key, children: [] } as MotifRelationship;
                    });
                    setExhibitionFacetCounts(counts);
                    setFilterAllExhibitions({ parentName: props.translations.filterHeaderExhibitions, children });
                }
            })
            .catch((error) => {
                console.error("Error fetching filter data:", error);
            });
    };

    const buildURLParams = (searchQuery: string, pageNumber: number, sp: SearchParams): URLSearchParams => {
        const urlParams = new URLSearchParams();
        urlParams.set("query", searchQuery);
        urlParams.set("page", pageNumber.toString());

        if (sp.sortingOption && sp.sortingOption !== "default") {
            urlParams.set("sortOrder", sp.sortingOption);
        }

        if (sp.showMainCollection) {
            urlParams.set("munchSamlingen", "true");
        }
        if (sp.showDisplayed) {
            urlParams.set("showDisplayed", "true");
        }
        if (sp.showAll) {
            urlParams.set("showAll", "true");
        }
        if (sp.classification) {
            urlParams.set("classification", sp.classification);
        }

        const yearsParam = `${minYear}|${maxYear}`;
        urlParams.set("years", yearsParam);

        sp.themes.forEach((theme) => {
            urlParams.append("theme", theme);
        });
        sp.motifs.forEach((motif) => {
            urlParams.append("motif", motif);
        });
        sp.exhibitions.forEach((exhibition) => {
            urlParams.append("exhibition", exhibition);
        });
        return urlParams;
    };

    const buildQueryString = (searchQuery: string, pageNumber: number, sp: SearchParams): string => {
        const urlParams = buildURLParams(searchQuery, pageNumber, sp);
        return `?${urlParams.toString()}`;
    };

    const updateURL = (searchQuery: string, pageNumber: number, sp: SearchParams): void => {
        const urlParams = buildURLParams(searchQuery, pageNumber, sp);
        window.history.replaceState(
            { ...window.history.state },
            "",
            `${window.location.pathname}?${urlParams.toString()}`
        );
        const searchState = {
            query: searchQuery,
            searchUrlParams: urlParams.toString(),
        };
        sessionStorage.setItem("searchState", JSON.stringify(searchState));
    };

    function getStringForLanguageWithFallback(norwegianString: string, englishString: string, language: string) {
        if (language === "en" && englishString) return englishString;
        return norwegianString;
    }

    const itemsDisplayedText = props.itemsDisplayedText
        .replace("{0}", formatCount(totalCountIncludingHidden))
        .replace("{1}", formatCount(totalCount));
    return (
        <div className="CollectionNewSearchPage">
            <div className="CollectionNewSearchPage__container">
                <div className="CollectionNewSearchPage__header__container">
                    <h1 className="CollectionNewSearchPage__header">{props.heading}</h1>
                    <div className="CollectionNewSearchPage__description">
                        <p>{props.description}</p>
                    </div>
                </div>
                <CollectionNewSearch
                    query={query}
                    onChange={(e) => setQuery(e.target.value)}
                    handleSearch={(word) => handleSearchChange(word ?? query)}
                    searchParams={searchParams}
                    setSearchParams={setSearchParamsWrapper}
                    props={props}
                    setMinYear={setMinYear}
                    setMaxYear={setMaxYear}
                    minYear={minYear}
                    maxYear={maxYear}
                    activeFilters={activeFilters}
                    filterAllMotifs={filterAllMotifs}
                    filterAllThemes={filterAllThemes}
                    filterAllExhibitions={filterAllExhibitions}
                    motifFacetCounts={motifFacetCounts}
                    themeFacetCounts={themeFacetCounts}
                    exhibitionFacetCounts={exhibitionFacetCounts}
                    totalCount={totalCount}
                    filterIsOpen={filterIsOpen}
                    setFilterIsOpen={setFilterIsOpen}
                />

                <div className="CollectionNewSearchPage__search-results" ref={resultsRef}>
                    {results.length === 0 ? (
                        resultIsLoading ? (
                            <div className="CollectionNewSearchPage__loader">
                                <Loader />
                            </div>
                        ) : (
                            <strong className="CollectionNewSearchPage__no-results">{props.noResultsText}</strong>
                        )
                    ) : (
                        <>
                            <p className="CollectionNewSearchPage__result-count">
                                {itemsDisplayedText}
                            </p>

                            <CollectionList
                                items={results.map((item: any) => ({
                                    title: getStringForLanguageWithFallback(item.title, item.titleEn, props.language),
                                    medium: getStringForLanguageWithFallback(
                                        item.medium,
                                        item.mediumEn,
                                        props.language
                                    ),
                                    primaryMedia: item.primaryMedia,
                                    mediaId: item.mediaId || "",
                                    displayDate: item.displayDate,
                                    invNo: item.invNo,
                                    linkToObjectPage: props.collectionSearchResults.linkToObjectPage,
                                    isVirtualObject: item.isVirtualObject,
                                    partOfExhibitions: (item.partOfExhibitions ?? []).map((ex: any) => ({
                                        exhibitionTitle: getStringForLanguageWithFallback(ex.exhibitionTitle, ex.exhibitionTitleEnglish, props.language),
                                        exhibitionPageLink: getStringForLanguageWithFallback(ex.exhibitionPageLink, ex.exhibitionPageLinkEnglish, props.language),
                                        id: ex.id,
                                        displayDate: ex.displayDate,
                                    })),
                                    numberOfChildren: item.numberOfChildren,
                                    childrenPrimaryMedia: item.childrenPrimaryMedia,
                                    onDisplayLabel: props.exhibitedIn,
                                    versionsLabel: props.versionsLabel,
                                }))}
                            />
                            {hasMoreResults && (
                                <div className="CollectionNewSearchPage__show-more-button">
                                    <button onClick={handleShowMore}>{props.showMoreText}</button>
                                </div>
                            )}
                            {resultIsLoading && (
                                <div className="CollectionNewSearchPage__loader">
                                    <Loader />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
